import React from 'react';
import { Clock, Mail, CheckCircle } from 'lucide-react';

interface PendingVerificationNoticeProps {
  userRole?: string;
  onRetry?: () => void;
}

const PendingVerificationNotice: React.FC<PendingVerificationNoticeProps> = ({ 
  userRole = 'provider',
  onRetry 
}) => {
  const isTherapist = userRole === 'THERAPIST';
  const isClinician = userRole === 'CLINICIAN';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Verification Pending
            </h1>
            <p className="text-gray-600 mb-6">
              Your {isTherapist ? 'therapist' : isClinician ? 'clinician' : 'provider'} verification is currently under review.
            </p>
          </div>
          
          <div className="px-6 pb-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <CheckCircle className="h-4 w-4 text-blue-400" />
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    We've received your verification documents and are reviewing them. This process typically takes 1-3 business days.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100">
                      <span className="text-xs font-medium text-blue-600">1</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      Our team reviews your credentials and documentation
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100">
                      <span className="text-xs font-medium text-blue-600">2</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      We verify your license and professional qualifications
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100">
                      <span className="text-xs font-medium text-blue-600">3</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      You'll receive an email notification once verification is complete
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Need help?
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    If you have questions about your verification status, please contact our support team.
                  </p>
                  <a
                    href="mailto:support@tinywins.ai"
                    className="text-sm text-indigo-600 hover:text-indigo-500 mt-1 inline-block"
                  >
                    support@tinywins.ai
                  </a>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Go to Dashboard
              </button>
              
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Update Verification
                </button>
              )}
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                You can continue using basic features while your verification is pending.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingVerificationNotice;
